import { api } from './api';

/**
 * Chat (M-chat) house-side client. The shared admin support inbox — every
 * conversation here is `seller_kind='house'`. Gated server-side by the
 * `view.messages` permission. Polling-first, same cadence as the member side.
 */

export type SenderKind = 'buyer' | 'seller';

export type AdminConversationListItem = {
  id: string;
  sellerKind: 'house' | 'merchant';
  /** The buyer's name or email (the counterparty for the house side). */
  counterparty: string | null;
  productId: string | null;
  orderId: string | null;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  lastMessageSenderKind: SenderKind | null;
  unread: boolean;
  /** The buyer's user id — target for the Block / Unblock action. */
  buyerUserId: string;
  /** Whether the buyer is currently blocked from messaging. */
  buyerChatBlocked: boolean;
};

export type AdminConversationsPage = {
  items: AdminConversationListItem[];
  nextCursor: string | null;
};

export type ChatMessage = {
  id: string;
  senderKind: SenderKind;
  senderUserId: string | null;
  body: string;
  createdAt: string;
};

export type MessagesPage = { items: ChatMessage[]; nextCursor: string | null };

export async function listAdminConversations(
  args: { limit?: number; cursor?: string } = {},
): Promise<AdminConversationsPage> {
  const res = await api.get<AdminConversationsPage>('/admin/conversations', { params: args });
  return res.data;
}

export async function getAdminChatUnreadCount(): Promise<{ count: number }> {
  const res = await api.get<{ count: number }>('/admin/conversations/unread-count');
  return res.data;
}

export async function listAdminChatMessages(
  conversationId: string,
  args: { limit?: number; cursor?: string } = {},
): Promise<MessagesPage> {
  const res = await api.get<MessagesPage>(`/admin/conversations/${conversationId}/messages`, {
    params: args,
  });
  return res.data;
}

export async function sendAdminChatMessage(
  conversationId: string,
  body: string,
): Promise<ChatMessage> {
  const res = await api.post<ChatMessage>(`/admin/conversations/${conversationId}/messages`, {
    body,
  });
  return res.data;
}

export async function markAdminConversationRead(conversationId: string): Promise<void> {
  await api.post(`/admin/conversations/${conversationId}/read`);
}

/** Block or unblock a member from messaging (admin-only, from the inbox). */
export async function setUserChatBlocked(
  userId: string,
  blocked: boolean,
): Promise<{ ok: boolean; chatBlocked: boolean }> {
  const res = await api.patch<{ ok: boolean; chatBlocked: boolean }>(
    `/users/${userId}/chat-block`,
    { blocked },
  );
  return res.data;
}
